/**
 * Mirrors `ForemanPhaseDto` (apps/sparta-api/UPS.Sparta.DTO/Timekeeping/Foreman).
 *
 * The four fields below are NULLABLE and NOT OPTIONAL, and both halves of that are
 * deliberate (#30151):
 *
 * - Nullable, because the C# DTO declares `ForemanPhaseID` and `PhaseID` as `Guid?`,
 *   and its project sets `<Nullable>warnings</Nullable>`, which leaves the annotation
 *   context OFF so `string PhaseName` / `string Description` carry no non-null
 *   guarantee either. This interface previously declared all four required and
 *   non-nullable, which overstated the contract its own API emits.
 * - NOT optional, because the API applies no null-ignoring serialization policy, so
 *   the property is always PRESENT on the wire and may be null. Marking them `?`
 *   would model an absence that does not occur, while saying nothing about the null
 *   case that does.
 *
 * ⚠️ This is an HONEST CONTRACT, not an enforced one. `strictNullChecks` is OFF
 * workspace-wide (unset in `tsconfig.base.json`, verified behaviourally), so the
 * compiler currently ignores `| null` entirely. Consumers that must handle null
 * have to do so deliberately, as `ForemanPhaseLike` in `@ups/xplat/utils` does.
 */
export interface ForemanPhase {
  foremanPhaseID: string | null;
  phaseID: string | null;
  phaseName: string | null;
  description: string | null;
  /** False for non-billable phases (VAC/HOL entry allowed). */
  isBillable?: boolean;
  /**
   * Viewpoint per-phase timesheet block (bJCJP.udTimesheetBlock, 'Y' = blocked). #30262.
   * Optional (not just nullable): an older API omits it entirely during rollout. Read it
   * through `isWorkOrderTimesheetBlocked` from `@ups/xplat/utils`, never raw comparison.
   */
  udTimesheetBlock?: string | null;
}
